import type { GroupLifeMemberCrudResponse } from '@/services/group-life';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PmeLifeRow } from '../../components/pme/proposal.mock';
import { fetchMembersPage, membersPageKey } from './use-lives-count.hook';

function toLifeRow(
  member: GroupLifeMemberCrudResponse,
  targetId: string,
  position: number
): PmeLifeRow {
  const memberId =
    member.id ??
    (member.policyElementId != null
      ? String(member.policyElementId)
      : undefined);
  return {
    id: memberId ?? `life-${targetId}-${position}`,
    status: member.awayFromWork ? 'inactive' : 'active',
    insurableGroup: member.insurableGroup || '—',
    capital: member.capital || '—',
    sex: member.gender || '—',
    birthDate: member.dateOfBirth || '—',
    name: member.name || '—',
    memberId,
    targetId,
    cpf: member.cpf || '',
    gender: member.gender || '',
    cids: member.cids ?? [],
    awayFromWork: !!member.awayFromWork,
  };
}

export type GroupLifeTargetLivesPage = {
  /** Vidas da página pedida deste target. */
  lives: PmeLifeRow[];
  /** Total de vidas do target, contado pelo backend. */
  total: number;
  /** Primeira carga. */
  isLoading: boolean;
  /** Trocando de página. */
  isFetching: boolean;
};

/**
 * Vidas de UM target (estipulante master ou um subestipulante) paginadas NO
 * SERVIDOR (`/members/page`) — cada estipulante tem a própria tabela, como no
 * passo de vidas da cotação.
 */
export function useGroupLifeTargetLivesPage(params: {
  policyId: string | undefined;
  targetId: string;
  pageNo: number;
  pageSize: number;
}): GroupLifeTargetLivesPage {
  const { policyId, targetId, pageNo, pageSize } = params;

  const query = useQuery({
    queryKey: membersPageKey(policyId ?? '', targetId, pageNo, pageSize),
    queryFn: () => fetchMembersPage(policyId ?? '', targetId, pageNo, pageSize),
    enabled: !!policyId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const pageOffset = (pageNo - 1) * pageSize;
  const lives = (query.data?.members ?? []).map((member, position) =>
    toLifeRow(member, targetId, pageOffset + position)
  );

  return {
    lives,
    total: query.data?.total ?? 0,
    isLoading: !!policyId && query.isLoading,
    isFetching: query.isFetching,
  };
}
